import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import type { ReactNode } from 'react'
import axios from 'axios'
import { buildApiUrl, ENDPOINTS } from '../config/api'

export interface User {
  id: number
  name: string
  email: string
  cpf?: string
  phone?: string
  avatar?: string
  user_profile_id: number
  last_organization_id?: number
  created_at: string
}

export interface Organization {
  organization_id: number
  organization_name: string
  profile_code: string
  profile_label: string
}

export interface MenuItem {
  id: string
  code: string
  label: string
  path: string | null
  icon: string | null
  display_order: number
  submenu?: MenuItem[]
}

interface AccessProfile {
  code: string
  label: string
}

interface AuthContextType {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  isSwitching: boolean
  organizations: Organization[]
  activeOrganization: { id: number; name: string } | null
  accessProfile: AccessProfile | null
  leaderId: number | null
  leaderPermissions: { can_register_voters: boolean; can_view_all_voters: boolean } | null
  menuItems: MenuItem[]
  login: (email: string, password: string, remember?: boolean) => Promise<void>
  logout: () => void
  switchOrganization: (organizationId: number) => Promise<void>
  updateUser: (user: User) => void
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('token'))
  const [isLoading, setIsLoading] = useState(true)
  const [isSwitching, setIsSwitching] = useState(false)
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [activeOrganization, setActiveOrganization] = useState<{ id: number; name: string } | null>(null)
  const [accessProfile, setAccessProfile] = useState<AccessProfile | null>(null)
  const [leaderId, setLeaderId] = useState<number | null>(null)
  const [leaderPermissions, setLeaderPermissions] = useState<{ can_register_voters: boolean; can_view_all_voters: boolean } | null>(null)
  const [menuItems, setMenuItems] = useState<MenuItem[]>([])

  const logout = useCallback(() => {
    setUser(null)
    setToken(null)
    setOrganizations([])
    setActiveOrganization(null)
    setAccessProfile(null)
    setLeaderId(null)
    setLeaderPermissions(null)
    setMenuItems([])
    localStorage.removeItem('token')
    delete axios.defaults.headers.common['Authorization']
  }, [])

  const processMe = useCallback((data: any) => {
    setUser(data.user)
    setOrganizations(data.organizations || [])
    if (data.organization) {
      setActiveOrganization({ id: data.organization.id, name: data.organization.name })
    }
    if (data.access_profile) {
      setAccessProfile(data.access_profile)
    }
    setLeaderId(data.leader_id || null)
    setLeaderPermissions(data.leader_permissions || null)
    if (data.menu_items?.length > 0) {
      setMenuItems(data.menu_items)
    }
  }, [])

  const fetchUser = useCallback(async () => {
    if (!token) {
      setIsLoading(false)
      return
    }

    try {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
      const response = await axios.get(buildApiUrl(ENDPOINTS.ME))
      processMe(response.data.data)
    } catch {
      logout()
    } finally {
      setIsLoading(false)
    }
  }, [token, logout, processMe])

  useEffect(() => {
    fetchUser()
  }, [fetchUser])

  // Auto-refresh: interceptor 401 tenta refresh antes de deslogar
  const isRefreshing = useRef(false)
  const failedQueue = useRef<Array<{ resolve: (token: string) => void; reject: (err: any) => void }>>([])

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config
        if (error.response?.status !== 401 || originalRequest._retry || originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/refresh')) {
          return Promise.reject(error)
        }

        if (isRefreshing.current) {
          return new Promise((resolve, reject) => {
            failedQueue.current.push({ resolve, reject })
          }).then(newToken => {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`
            return axios(originalRequest)
          })
        }

        originalRequest._retry = true
        isRefreshing.current = true

        try {
          const res = await axios.post(buildApiUrl(ENDPOINTS.REFRESH))
          const newToken = res.data.token
          setToken(newToken)
          localStorage.setItem('token', newToken)
          axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`
          failedQueue.current.forEach(p => p.resolve(newToken))
          failedQueue.current = []
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`
          return axios(originalRequest)
        } catch {
          failedQueue.current.forEach(p => p.reject(error))
          failedQueue.current = []
          logout()
          return Promise.reject(error)
        } finally {
          isRefreshing.current = false
        }
      }
    )
    return () => axios.interceptors.response.eject(interceptor)
  }, [logout])

  const login = async (email: string, password: string, remember?: boolean) => {
    const response = await axios.post(buildApiUrl(ENDPOINTS.LOGIN), { email, password, remember: !!remember })
    const { token: newToken, user: userData } = response.data
    setToken(newToken)
    setUser(userData)
    localStorage.setItem('token', newToken)
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`

    // Buscar dados completos (/me)
    const meResponse = await axios.get(buildApiUrl(ENDPOINTS.ME))
    processMe(meResponse.data.data)
  }

  const switchOrganization = async (organizationId: number) => {
    setIsSwitching(true)

    // 1. Switch no backend
    await axios.post(buildApiUrl('/v1/organizations/switch'), { organization_id: organizationId })

    // 2. Reload completo via /me
    const meResponse = await axios.get(buildApiUrl(ENDPOINTS.ME))
    processMe(meResponse.data.data)

    // 3. Pequeno delay para a splash aparecer
    await new Promise(resolve => setTimeout(resolve, 800))
    setIsSwitching(false)
  }

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser)
  }

  return (
    <AuthContext.Provider value={{
      user,
      token,
      isAuthenticated: !!user,
      isLoading,
      isSwitching,
      organizations,
      activeOrganization,
      accessProfile,
      leaderId,
      leaderPermissions,
      menuItems,
      login,
      logout,
      switchOrganization,
      updateUser,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
